import axios from 'axios'

// En production, utiliser une URL relative pour passer par Nginx
// En développement, utiliser localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.PROD ? '/api' : 'http://localhost:5001/api')

// Configuration axios par défaut
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Intercepteur pour gérer les erreurs et refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          })

          const { tokens } = response.data
          localStorage.setItem('accessToken', tokens.accessToken)
          
          // Refaire la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        // Refresh token invalide, rediriger vers login
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// API d'authentification
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/change-password', { currentPassword, newPassword }),
}

// API des quiz
export const quizAPI = {
  getAll: (params = {}) => api.get('/quiz', { params }),
  getById: (id) => api.get(`/quiz/${id}`),
  create: (quizData) => api.post('/quiz', quizData),
  update: (id, quizData) => api.put(`/quiz/${id}`, quizData),
  delete: (id) => api.delete(`/quiz/${id}`),
  publish: (id) => api.patch(`/quiz/${id}/publish`),
  archive: (id) => api.patch(`/quiz/${id}/archive`),
  duplicate: (id) => api.post(`/quiz/${id}/duplicate`),
  getStats: (id) => api.get(`/quiz/${id}/stats`),
  generateShareLink: (id) => api.post(`/quiz/${id}/share-link`),
}

// API des tentatives de quiz
export const attemptAPI = {
  startAttempt: (quizId) => api.post(`/quiz/${quizId}/attempt`),
  submitAnswer: (attemptId, questionId, answer) => 
    api.post(`/attempt/${attemptId}/answer`, { questionId, answer }),
  submitAttempt: (attemptId) => api.post(`/attempt/${attemptId}/submit`),
  getAttempt: (attemptId) => api.get(`/attempt/${attemptId}`),
  getAttemptsByUser: (params = {}) => api.get('/attempt/my-attempts', { params }),
}

// API des utilisateurs/étudiants
export const userAPI = {
  getAll: (params = {}) => api.get('/user', { params }),
  getById: (id) => api.get(`/user/${id}`),
  update: (id, userData) => api.put(`/user/${id}`, userData),
  delete: (id) => api.delete(`/user/${id}`),
  suspend: (id) => api.patch(`/user/${id}/suspend`),
  activate: (id) => api.patch(`/user/${id}/activate`),
  setPassword: (id, password) => api.patch(`/user/${id}/password`, { password }),
  getStats: (id) => api.get(`/user/${id}/stats`),
  exportResults: (id, format = 'pdf') => 
    api.get(`/user/${id}/export`, { params: { format }, responseType: 'blob' }),
}

// API des médias
export const mediaAPI = {
  upload: (file, type) => {
    const formData = new FormData()
    formData.append(type, file)
    return api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  delete: (filename) => api.delete(`/media/${filename}`),
  getUrl: (filename) => `${API_BASE_URL.replace('/api', '')}/uploads/${filename}`,
}

// API Telegram
export const telegramAPI = {
  sendQuizNotification: (quizId, message) => 
    api.post('/telegram/send-notification', { quizId, message }),
  getBotInfo: () => api.get('/telegram/bot-info'),
  generateWebAppUrl: (quizId) => api.post(`/telegram/webapp-url/${quizId}`),
}

// API des catégories
export const categoryAPI = {
  getAll: (params = {}) => api.get('/category', { params }),
  getById: (id) => api.get(`/category/${id}`),
  create: (categoryData) => api.post('/category', categoryData),
  update: (id, categoryData) => api.put(`/category/${id}`, categoryData),
  delete: (id) => api.delete(`/category/${id}`),
}

export default api
